import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, doc, getDoc, setDoc, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import { Search, Save, Eye, Globe, Share2, Lock, AlertCircle, CheckCircle, Loader2, Sparkles, Crown, ArrowRight } from "lucide-react";

interface CourseSEO {
  courseId: string;
  courseName: string;
  title: string;
  description: string;
  keywords: string;
  ogImage?: string;
  enabled: boolean;
}

interface InstructorSEO {
  title: string;
  description: string;
  keywords: string;
  ogImage?: string;
  enabled: boolean;
}

export default function InstructorSEO() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [hasProPlus, setHasProPlus] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'courses'>('profile');
  const [previewMode, setPreviewMode] = useState<'google' | 'facebook'>('google');

  // Profile SEO
  const [profileSEO, setProfileSEO] = useState<InstructorSEO>({
    title: "",
    description: "",
    keywords: "",
    ogImage: "",
    enabled: true,
  });

  // Courses SEO
  const [courses, setCourses] = useState<CourseSEO[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<CourseSEO | null>(null);

  useEffect(() => {
    if (user) {
      checkSubscription();
      loadSEOSettings();
      loadCourses();
    }
  }, [user]);

  const checkSubscription = async () => {
    if (!user) return;

    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const tier = userData.subscriptionTier || "free";
        const isProPlus = tier === "pro_plus_monthly" || tier === "pro_plus_yearly";
        setHasProPlus(isProPlus);

        if (!isProPlus) {
          setLoading(false);
        }
      }
    } catch (error) {
      console.error("Error checking subscription:", error);
      setLoading(false);
    }
  };

  const loadSEOSettings = async () => {
    if (!user) return;

    try {
      const docRef = doc(db, "instructorSEO", user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setProfileSEO({
          title: data.title || "",
          description: data.description || "",
          keywords: data.keywords || "",
          ogImage: data.ogImage || "",
          enabled: data.enabled !== false,
        });
      }
    } catch (error) {
      console.error("Error loading SEO settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    if (!user) return;

    try {
      const coursesQuery = query(
        collection(db, "courses"),
        where("instructorId", "==", user.uid)
      );
      const coursesSnap = await getDocs(coursesQuery);

      const coursesData: CourseSEO[] = [];
      for (const courseDoc of coursesSnap.docs) {
        const course = courseDoc.data();
        
        // Load SEO data for this course
        const seoDoc = await getDoc(doc(db, "courseSEO", courseDoc.id));
        const seoData = seoDoc.exists() ? seoDoc.data() : {};

        coursesData.push({
          courseId: courseDoc.id,
          courseName: course.title,
          title: seoData.title || course.title,
          description: seoData.description || course.description || "",
          keywords: seoData.keywords || "",
          ogImage: seoData.ogImage || course.thumbnailUrl || "",
          enabled: seoData.enabled !== false,
        });
      }

      setCourses(coursesData);
      if (coursesData.length > 0) {
        setSelectedCourse(coursesData[0]);
      }
    } catch (error) {
      console.error("Error loading courses:", error);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setSaving(true);
    setSuccess(false);

    try {
      await setDoc(doc(db, "instructorSEO", user.uid), {
        ...profileSEO,
        instructorId: user.uid,
        updatedAt: serverTimestamp(),
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving profile SEO:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCourse = async () => {
    if (!user || !selectedCourse) return;

    setSaving(true);
    setSuccess(false);

    try {
      await setDoc(doc(db, "courseSEO", selectedCourse.courseId), {
        ...selectedCourse,
        instructorId: user.uid,
        updatedAt: serverTimestamp(),
      });

      // Update local state
      setCourses(courses.map(c => 
        c.courseId === selectedCourse.courseId ? selectedCourse : c
      ));

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving course SEO:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Pro+ Only Feature
  if (!hasProPlus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex items-center justify-center p-6">
        <div className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">ฟีเจอร์สำหรับ Pro+ เท่านั้น</h2>
          <p className="text-gray-600 mb-6">
            SEO Optimization เป็นฟีเจอร์พิเศษสำหรับผู้ใช้ Pro+ ที่จะช่วยให้คอร์สของคุณติด Google Search
          </p>

          <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Crown className="w-6 h-6 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900">ฟีเจอร์ Pro+ SEO</h3>
            </div>
            <ul className="space-y-2 text-left text-gray-700">
              <li className="flex items-start gap-2">
                <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>ทำให้คอร์สของคุณติด Google Search</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>ปรับแต่ง Title, Description, Keywords</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Rich Preview เมื่อแชร์บน Social Media</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>เพิ่มโอกาสหาลูกค้าใหม่ผ่าน Organic Search</span>
              </li>
            </ul>
          </div>

          <button
            onClick={() => navigate("/pricing")}
            className="w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold text-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
          >
            <Crown className="w-5 h-5" />
            อัปเกรดเป็น Pro+ เลย
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-pink-600 rounded-xl flex items-center justify-center">
              <Search className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold text-gray-900">SEO Optimization</h1>
                <span className="px-3 py-1 bg-gradient-to-r from-blue-600 to-pink-600 text-white text-xs font-bold rounded-full">
                  PRO+
                </span>
              </div>
              <p className="text-gray-600">ทำให้คอร์สของคุณติด Google Search</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'profile'
                ? 'bg-gradient-to-r from-blue-600 to-pink-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            SEO โปรไฟล์ผู้สอน
          </button>
          <button
            onClick={() => setActiveTab('courses')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              activeTab === 'courses'
                ? 'bg-gradient-to-r from-blue-600 to-pink-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            SEO คอร์สเรียน ({courses.length})
          </button>
        </div>

        {/* Profile SEO Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">SEO โปรไฟล์ผู้สอน</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Title Tag
                  </label>
                  <input
                    type="text"
                    value={profileSEO.title}
                    onChange={(e) => setProfileSEO({ ...profileSEO, title: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                    placeholder="ชื่อที่จะแสดงใน Google Search"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {profileSEO.title.length}/60 ตัวอักษร
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Meta Description
                  </label>
                  <textarea
                    value={profileSEO.description}
                    onChange={(e) => setProfileSEO({ ...profileSEO, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                    placeholder="คำอธิบายเกี่ยวกับคุณและคอร์สที่สอน"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {profileSEO.description.length}/160 ตัวอักษร
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Keywords
                  </label>
                  <input
                    type="text"
                    value={profileSEO.keywords}
                    onChange={(e) => setProfileSEO({ ...profileSEO, keywords: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                    placeholder="คำค้นหา, แยกด้วยเครื่องหมายจุลภาค"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Open Graph Image URL
                  </label>
                  <input
                    type="text"
                    value={profileSEO.ogImage}
                    onChange={(e) => setProfileSEO({ ...profileSEO, ogImage: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                    placeholder="URL รูปภาพสำหรับ Social Media"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={profileSEO.enabled}
                    onChange={(e) => setProfileSEO({ ...profileSEO, enabled: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label className="text-sm text-gray-700">เปิดใช้งาน SEO</label>
                </div>
              </div>

              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    กำลังบันทึก...
                  </>
                ) : success ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    บันทึกสำเร็จ!
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    บันทึกการเปลี่ยนแปลง
                  </>
                )}
              </button>
            </div>

            {/* Preview */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-600" />
                  ตัวอย่างการแสดงผล
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPreviewMode('google')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      previewMode === 'google' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <Globe className="w-4 h-4 inline mr-1" />
                    Google
                  </button>
                  <button
                    onClick={() => setPreviewMode('facebook')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      previewMode === 'facebook' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <Share2 className="w-4 h-4 inline mr-1" />
                    Facebook
                  </button>
                </div>
              </div>

              {previewMode === 'google' && (
                <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="text-xs text-gray-500 mb-1">
                    https://edumi.app/instructor/{user?.uid}
                  </div>
                  <div className="text-xl text-blue-600 hover:underline cursor-pointer mb-1">
                    {profileSEO.title || "ชื่อผู้สอน"}
                  </div>
                  <div className="text-sm text-gray-700">
                    {profileSEO.description || "คำอธิบายเกี่ยวกับผู้สอน"}
                  </div>
                </div>
              )}

              {previewMode === 'facebook' && (
                <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-white">
                  {profileSEO.ogImage && (
                    <div className="w-full h-64 bg-gray-200">
                      <img src={profileSEO.ogImage} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="text-xs text-gray-500 uppercase mb-1">EDUMI.APP</div>
                    <div className="font-bold text-gray-900 mb-1">{profileSEO.title}</div>
                    <div className="text-sm text-gray-600">{profileSEO.description}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Courses SEO Tab */}
        {activeTab === 'courses' && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Course List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h3 className="font-bold text-gray-900 mb-4">คอร์สของคุณ</h3>
                {courses.length === 0 ? (
                  <p className="text-sm text-gray-500">ยังไม่มีคอร์ส</p>
                ) : (
                  <div className="space-y-2">
                    {courses.map((course) => (
                      <button
                        key={course.courseId}
                        onClick={() => setSelectedCourse(course)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                          selectedCourse?.courseId === course.courseId
                            ? "bg-gradient-to-r from-blue-600 to-pink-600 text-white"
                            : "bg-gray-50 hover:bg-gray-100 text-gray-700"
                        }`}
                      >
                        <div className="font-semibold text-sm">{course.courseName}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Course SEO Form */}
            <div className="lg:col-span-2">
              {selectedCourse ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">
                    SEO: {selectedCourse.courseName}
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Title Tag
                      </label>
                      <input
                        type="text"
                        value={selectedCourse.title}
                        onChange={(e) => setSelectedCourse({ ...selectedCourse, title: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedCourse.title.length}/60 ตัวอักษร
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Meta Description
                      </label>
                      <textarea
                        value={selectedCourse.description}
                        onChange={(e) => setSelectedCourse({ ...selectedCourse, description: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedCourse.description.length}/160 ตัวอักษร
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Keywords
                      </label>
                      <input
                        type="text"
                        value={selectedCourse.keywords}
                        onChange={(e) => setSelectedCourse({ ...selectedCourse, keywords: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedCourse.enabled}
                        onChange={(e) => setSelectedCourse({ ...selectedCourse, enabled: e.target.checked })}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <label className="text-sm text-gray-700">เปิดใช้งาน SEO</label>
                    </div>
                  </div>

                  <button
                    onClick={handleSaveCourse}
                    disabled={saving}
                    className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        กำลังบันทึก...
                      </>
                    ) : success ? (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        บันทึกสำเร็จ!
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        บันทึกการเปลี่ยนแปลง
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">เลือกคอร์สเพื่อแก้ไข SEO</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-2">เคล็ดลับ SEO:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Title ควรมี 50-60 ตัวอักษร และมีคำค้นหาสำคัญ</li>
                <li>Description ควรมี 150-160 ตัวอักษร อธิบายชัดเจน</li>
                <li>ใส่ Keywords ที่เกี่ยวข้องกับคอร์สของคุณ</li>
                <li>อัปเดต SEO เป็นประจำเพื่อผลลัพธ์ที่ดีขึ้น</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
